import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useServerStatus } from '@/contexts/server-status-context'
import { AuthLoopPage } from '@/contexts/auth-loop-page'
import { setRedirecting } from '@/lib/auth-redirect-flag'
import {
  AUTH_BOUNCE_THRESHOLD,
  clearBounceCount,
  incrementBounceCount,
  onAuthLoopDetected,
} from '@/lib/auth-bounce-counter'

let currentPermissions: string[] = []
export function hasGlobalPermission(permission: string): boolean {
  return currentPermissions.includes(permission)
}

export interface AuthUser {
  id: string
  identityId: string | null
  partyId: string | null
  email: string
  name: string
  roles: string[]
  permissions: string[]
  impersonating: boolean
  originalUser: { name: string; email: string } | null
}

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => void
  logout: () => void
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  isImpersonating: boolean
  startImpersonation: (targetAuthUserId: string) => Promise<void>
  stopImpersonation: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const API_BASE = ''

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authLoopDetected, setAuthLoopDetected] = useState(false)
  const { setServerAvailable } = useServerStatus()

  const fetchMe = useCallback(async () => {
    setServerAvailable(true)
    setAuthLoopDetected(false)
    let response: Response
    try {
      response = await fetch(`${API_BASE}${endpoints.auth.me()}`, {
        credentials: 'include',
      })
    } catch (error) {
      console.error('Auth check failed (network error):', error)
      setServerAvailable(false)
      setIsLoading(false)
      return
    }

    if (response.ok) {
      const userData = await response.json()
      const perms = userData.permissions || []
      currentPermissions = perms
      setUser({
        id: userData.id,
        identityId: userData.identityId || null,
        partyId: userData.partyId || null,
        email: userData.email,
        name: userData.name,
        roles: userData.roles || [],
        permissions: perms,
        impersonating: userData.impersonating || false,
        originalUser: userData.originalUser || null,
      })
      setIsLoading(false)
      clearBounceCount()

      const returnTo = sessionStorage.getItem('returnTo')
      if (returnTo) {
        sessionStorage.removeItem('returnTo')
        if (returnTo !== window.location.pathname + window.location.search) {
          window.location.replace(returnTo)
        }
      }
      return
    }

    if (response.status >= 500) {
      console.error('Auth check failed (server error):', response.status)
      setServerAvailable(false)
      setIsLoading(false)
      return
    }

    if (response.status === 401) {
      const count = incrementBounceCount()
      if (count > AUTH_BOUNCE_THRESHOLD) {
        console.warn('Auth loop detected after', count, 'bounces in the same minute')
        setAuthLoopDetected(true)
        setIsLoading(false)
        return
      }
      console.log('Not authenticated, redirecting to Keycloak...')
      setRedirecting(true)
      sessionStorage.setItem('returnTo', window.location.pathname + window.location.search)
      window.location.href = `${API_BASE}${endpoints.auth.keycloakLogin()}`
      return
    }

    console.error('Auth check failed with unexpected status:', response.status)
    setServerAvailable(false)
    setIsLoading(false)
  }, [setServerAvailable])

  useEffect(() => {
    return onAuthLoopDetected(() => {
      setAuthLoopDetected(true)
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    fetchMe()
  }, [fetchMe])

  const login = useCallback(() => {
    window.location.href = `${API_BASE}${endpoints.auth.keycloakLogin()}`
  }, [])

  const logout = useCallback(async () => {
    clearBounceCount()
    try {
      const response = await fetch(`${API_BASE}${endpoints.auth.logout()}`, {
        method: 'POST',
        credentials: 'include',
      })
      if (response.ok) {
        const data = await response.json()
        window.location.href = data.logoutUrl
      } else {
        window.location.href = `${API_BASE}${endpoints.auth.keycloakLogin()}`
      }
    } catch (err) {
      window.location.href = `${API_BASE}${endpoints.auth.keycloakLogin()}`
      throw err
    }
  }, [])

  const hasPermission = useCallback((permission: string) => {
    const perms = user?.permissions
    return perms?.includes('*') || perms?.includes(permission) || false
  }, [user])

  const hasAnyPermission = useCallback((permissions: string[]) => {
    const perms = user?.permissions
    return perms?.includes('*') || permissions.some(p => perms?.includes(p) ?? false)
  }, [user])

  const startImpersonation = useCallback(async (targetAuthUserId: string) => {
    await api.post(endpoints.auth.impersonateStart(), { targetAuthUserId })
    await fetchMe()
  }, [fetchMe])

  const stopImpersonation = useCallback(async () => {
    await api.post(endpoints.auth.impersonateStop())
    await fetchMe()
  }, [fetchMe])

  const handleTryAgain = useCallback(() => {
    clearBounceCount()
    setAuthLoopDetected(false)
    setIsLoading(true)
    fetchMe()
  }, [fetchMe])

  if (authLoopDetected) {
    return <AuthLoopPage onTryAgain={handleTryAgain} onLogoutFully={logout} />
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
        isImpersonating: user?.impersonating ?? false,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// WHY: during HMR-driven module reloads or StrictMode mount-order races,
// a consumer can briefly render before AuthProvider establishes its context.
// `useAuth` throws in that window — spamming the error log with 6+ "must be
// used within an AuthProvider" entries per navigation (issue 1035). Banners,
// menus and other passive surfaces that have nothing useful to render
// without auth should use `useOptionalAuth` and gracefully short-circuit
// to `null` on missing context instead of crashing.
export function useOptionalAuth() {
  return useContext(AuthContext)
}
